import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { LogoLockup } from "@/components/ui/LogoLockup";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useNavItems } from "@/hooks/useNavItems";
import { Menu, PanelLeft, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { AccountMenu } from "./AccountMenu";
import { MenuNavItem } from "./MenuNavItem";
import { NavLink } from "./NavLink";

export type NavBarProps = {
  onLeftSidebarToggle?: () => void;
  leftSidebarOpen?: boolean;
  leftSidebarControlsId?: string;
  /**
   * Whether to show the primary destination links (Clipboard, API Keys, Docs,
   * …) in the top-right. Hidden on shells that carry the persistent left rail,
   * which surfaces those links itself.
   */
  showPrimaryLinks?: boolean;
};

export const NavBar = ({
  onLeftSidebarToggle,
  leftSidebarOpen = false,
  leftSidebarControlsId = "left-sidebar",
  showPrimaryLinks = true,
}: NavBarProps) => {
  const { user } = useAuth();
  const [navMenuOpen, setNavMenuOpen] = useState(false);

  const closeNavMenu = () => setNavMenuOpen(false);
  const primaryNavLinks = useNavItems();

  return (
    <header className="sticky top-0 z-[50] h-14 shrink-0 border-b border-zinc-200/70 bg-white">
      <div className="flex h-14 w-full items-center justify-between px-4">
        <div className="flex min-w-0 items-center gap-2">
          {onLeftSidebarToggle && (
            <Button
              variant="ghost"
              size="icon"
              id="left-sidebar-toggle"
              className="-ml-2 shrink-0 rounded-[3px] text-zinc-600 hover:text-zinc-900 lg:hidden"
              onClick={onLeftSidebarToggle}
              aria-expanded={leftSidebarOpen}
              aria-controls={leftSidebarControlsId}
              aria-label={leftSidebarOpen ? "Close navigation" : "Open navigation"}
            >
              {leftSidebarOpen ?
                <X className="h-5 w-5" aria-hidden />
                : <PanelLeft className="h-5 w-5" aria-hidden />}
            </Button>
          )}
          <LogoLockup />
        </div>
        <div className="flex items-center gap-2 lg:gap-4">
          {showPrimaryLinks && (
            <div className="hidden items-center gap-4 lg:flex">
              {primaryNavLinks.map((link) => (
                <NavLink key={link.to} to={link.to}>
                  <span className="font-ui inline-flex items-center gap-1.5">
                    {link.label}
                    {link.beta && (
                      <span className="rounded bg-zinc-100 px-1 py-0.5 text-3xs font-semibold uppercase leading-none tracking-wide text-zinc-500">
                        Beta
                      </span>
                    )}
                  </span>
                </NavLink>
              ))}
            </div>
          )}

          {showPrimaryLinks && (
            <Popover modal open={navMenuOpen} onOpenChange={setNavMenuOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 rounded-[3px] text-zinc-600 hover:text-zinc-900 lg:hidden"
                  aria-expanded={navMenuOpen}
                  aria-label={navMenuOpen ? "Close menu" : "Open menu"}
                  title={navMenuOpen ? "Close menu" : "Open menu"}
                >
                  <Menu className="h-5 w-5" aria-hidden />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={8}>
                <div className="flex flex-col">
                  {primaryNavLinks.map((link) => (
                    <MenuNavItem key={link.to} to={link.to} onSelect={closeNavMenu}>
                      <span className="flex items-center gap-1.5">
                        {link.label}
                        {link.beta && (
                          <span className="rounded bg-zinc-100 px-1 py-0.5 text-3xs font-semibold uppercase leading-none tracking-wide text-zinc-500">
                            Beta
                          </span>
                        )}
                      </span>
                    </MenuNavItem>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Account lives in the workspace rail on shells that carry it
              (showPrimaryLinks=false); the top-right chip is for rail-less pages. */}
          {showPrimaryLinks &&
            (user ? (
              <AccountMenu
                trigger={
                  <button
                    type="button"
                    className="cursor-pointer rounded-full transition-all hover:ring-2 hover:ring-zinc-200"
                    aria-label="Account menu"
                    title="Account"
                  >
                    <Avatar user={user} size="sm" initialsOnly />
                  </button>
                }
              />
            ) : (
              <Link
                to="/login"
                className="rounded-[3px] bg-zinc-900 px-3 py-1 text-sm font-medium text-white transition-colors hover:bg-zinc-800"
              >
                Sign in
              </Link>
            ))}
        </div>
      </div>
    </header>
  );
};
